import styles from './index.module.css'

import type {
  Column,
  ColumnDef,
  ColumnOrderState,
  ColumnPinningState,
  ColumnResizeMode,
  ColumnSizingState,
  ExpandedState,
  RowSelectionState,
  SortingState,
  Updater,
  VisibilityState,
} from '@tanstack/react-table'
import {
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import cn from 'clsx'
import isEqual from 'lodash/isEqual'
import React, {
  ComponentProps,
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  clearDataTableSelection,
  selectDataTableItems,
} from '@disco/state-management/actions/SelectionActions'
import {
  getDataTableSelection,
  getSelectionIndexes,
} from '@disco/state-management/selectors/selection'
import { handleModifierKeysDataTableSelection } from '@disco/utils/selection'

import { SelectionContext } from 'contexts/selection'

import { DropdownMenu, Flex, IconButton, Table, Tooltip } from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { SearchTable } from './table-components'
import {
  isStickyPinned,
  reconcileColumnOrder,
  reorderColumns,
  sanitizeColumnId,
} from './table-utils'
import type { Row, WithId } from './types'

// Color is not exported from Radix, so we need to extract it from a radix component
type RadixColor = ComponentProps<typeof DropdownMenu.Item>['color']

export type RowAction = {
  label?: string
  icon?: React.ReactNode
  onClick?: () => void
  subActions?: RowAction[]
  color?: RadixColor
  shortcut?: string
  toolbar?: boolean
  /** When set, the icon-only action button is wrapped in a tooltip. */
  tooltip?: string
}

type Props<T extends WithId> = {
  data: T[]
  columns?: ColumnDef<T>[]
  columnResizeMode?: ColumnResizeMode
  loading?: boolean
  rowSelection?: RowSelectionState
  columnVisibility?: VisibilityState
  sorting?: SortingState
  columnPinning?: ColumnPinningState
  /** Should be a stable reference (e.g. wrapped in `useMemo` or `useCallback`) to avoid unnecessary re-renders. */
  rowSelectionCondition?: (_row: Row<T>) => boolean
  onRowSelectionChange?: (_rowSelection: RowSelectionState) => void
  onColumnVisibilityChange?: (_columnVisibility: VisibilityState) => void
  onSortingChange?: (_sorting: SortingState) => void
  columnOrder?: ColumnOrderState
  onColumnOrderChange?: (_columnOrder: ColumnOrderState) => void
  /** Enables drag-to-reorder column headers. Opt in explicitly - most tables don't persist the resulting order, which makes reordering more annoying than useful. */
  enableColumnDragging?: boolean
  columnSizing?: ColumnSizingState
  /** Called once sizing settles on a new value (drag end or an instant change like resetSize()) - not on every mousemove - so it's safe to persist. */
  onColumnSizingChange?: (_columnSizing: ColumnSizingState) => void
  getRowActions?: (_row: T) => RowAction[]
  stickyHeader?: boolean
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  onFetchNextPage?: () => void
  hasNextPageError?: boolean
  onRetryNextPage?: () => void
  isMultiSelect?: boolean
  location?: string
  searchType?: string
  searchResultsSource?: string
  searchId?: string
  meta?: Record<string, unknown>
  topRowContent?: React.ReactNode
  getSubRows?: (_row: T) => T[] | undefined
  expanded?: ExpandedState
  onExpandedChange?: (_expanded: ExpandedState) => void
}

const renderDropdownActions = (
  actions: RowAction[],
  onClickRowAction: (_action: RowAction) => void,
) => {
  return actions.map((action, i) => {
    if (action.subActions) {
      return (
        <DropdownMenu.Sub key={i}>
          <DropdownMenu.SubTrigger>
            {action.icon} {action.label}
          </DropdownMenu.SubTrigger>
          <DropdownMenu.SubContent>
            {renderDropdownActions(action.subActions, onClickRowAction)}
          </DropdownMenu.SubContent>
        </DropdownMenu.Sub>
      )
    } else {
      return action.label ? (
        <DropdownMenu.Item
          key={i}
          onSelect={() => onClickRowAction(action)}
          color={action.color}
          shortcut={action.shortcut}
        >
          {action.icon} {action.label}
        </DropdownMenu.Item>
      ) : (
        <DropdownMenu.Separator key={i} />
      )
    }
  })
}

const renderRowAction = (
  rowId: string,
  action: RowAction,
  onRowActionsOpenChange: (
    _rowId: string,
    _menu: string,
    _open: boolean,
  ) => void,
  onClickRowAction: (_action: RowAction) => void,
  rowActionsOpen: { id: string; menu: string } | null,
) => {
  if (action.subActions) {
    const menuLabel = action.label ?? ''
    return (
      <DropdownMenu.Root
        modal={false}
        open={rowActionsOpen?.id === rowId && rowActionsOpen.menu === menuLabel}
        onOpenChange={open => onRowActionsOpenChange(rowId, menuLabel, open)}
        key={action.label}
      >
        <DropdownMenu.Trigger>
          <IconButton variant="ghost" size="2" aria-label="Actions">
            {action.icon}
          </IconButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          {renderDropdownActions(action.subActions, onClickRowAction)}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    )
  }

  const button = (
    <IconButton
      key={action.label}
      variant="ghost"
      size="2"
      color={action.color}
      onClick={() => onClickRowAction(action)}
      aria-label={action.label}
    >
      {action.icon}
    </IconButton>
  )

  return action.tooltip ? (
    <Tooltip key={action.label} content={action.tooltip}>
      {button}
    </Tooltip>
  ) : (
    button
  )
}

const resolveUpdater = <T,>(updater: T | ((_old: T) => T), previous: T): T =>
  typeof updater === 'function'
    ? (updater as (_old: T) => T)(previous)
    : updater

const getPinningStyles = <T,>(column: Column<T>): CSSProperties => {
  const isPinned = isStickyPinned(column) ? column.getIsPinned() : false

  return {
    width: `calc(var(--col-${sanitizeColumnId(column.id)}-size) * 1px)`,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
  }
}

const alwaysSelectable = () => true

// Stable default so omitting `columns` doesn't churn the columns useMemo identity
const EMPTY_COLUMNS: never[] = []

/**
 * Advanced data table component with sorting, filtering, row selection, and infinite scrolling.
 *
 * Built on TanStack Table with customizable columns, actions, and comprehensive table functionality.
 * Supports row selection, column visibility controls, sorting, pinning, and infinite scroll pagination.
 *
 * @param data - Array of data objects to display in the table
 * @param columns - Column definitions for the table structure
 * @param loading - Whether the table is in a loading state
 * @param rowSelection - Current row selection state
 * @param columnVisibility - Current column visibility state
 * @param sorting - Current sorting state
 * @param onRowSelectionChange - Callback when row selection changes
 * @param onColumnVisibilityChange - Callback when column visibility changes
 * @param onSortingChange - Callback when sorting changes
 * @param getRowActions - Function to get available actions for each row
 * @param columnPinning - Column pinning configuration
 * @param stickyHeader - Whether the header should stick to the top (default: true)
 * @param hasNextPage - Whether there are more pages to load
 * @param isFetchingNextPage - Whether next page is currently being fetched
 * @param onFetchNextPage - Callback to fetch the next page of data
 * @see https://tanstack.com/table/latest - TanStack Table documentation
 * @see https://www.radix-ui.com/themes/docs/components/table - Radix UI Table documentation
 */
const DataTable = <T extends WithId>({
  data,
  columns: suppliedColumns = EMPTY_COLUMNS,
  loading,
  rowSelection,
  columnVisibility,
  sorting,
  onRowSelectionChange,
  rowSelectionCondition,
  onColumnVisibilityChange,
  onSortingChange,
  columnOrder,
  onColumnOrderChange,
  enableColumnDragging = false,
  columnSizing,
  onColumnSizingChange,
  getRowActions,
  columnPinning,
  stickyHeader = true,
  hasNextPage = false,
  isFetchingNextPage = false,
  onFetchNextPage,
  hasNextPageError = false,
  onRetryNextPage,
  isMultiSelect = false,
  location = '',
  searchType = '',
  searchResultsSource = '',
  searchId,
  meta,
  topRowContent,
  getSubRows,
  expanded,
  onExpandedChange,
}: Props<T>) => {
  const dispatch = useDispatch()
  const sentinelRef = useRef<HTMLTableRowElement>(null)
  const skeletonRef = useRef<HTMLTableRowElement>(null)
  const lastRowSelectedRef = useRef<number | null>(null)
  const onRowSelectionChangeRef = useRef(onRowSelectionChange)
  onRowSelectionChangeRef.current = onRowSelectionChange

  const [rowActionsOpen, setRowActionsOpen] = React.useState<{
    id: string
    menu: string
  } | null>(null)

  const selectionContext = useContext(SelectionContext)
  const selection = useSelector(state =>
    getSelectionIndexes(state, selectionContext.id),
  )

  const dataTableDataSelection = useSelector(state =>
    getDataTableSelection(state, selectionContext.id),
  )

  // Position in the rendered list, not TanStack's row.index - that one is
  // parent-relative, so sub-row indexes collide with top-level ones.
  const isPartOfMultipleSelection = useCallback(
    (flatIndex: number): boolean => selection.includes(flatIndex),
    [selection],
  )

  const onClickRowAction = useCallback((action: RowAction) => {
    action.onClick?.()
    setRowActionsOpen(null)
  }, [])

  const onRowActionsOpenChange = useCallback(
    (rowId: string, menu: string, open: boolean) => {
      if (open) {
        setRowActionsOpen({ id: rowId, menu })
      } else if (!open) {
        // Gives enough time to Radix to properly close the menu before removing the class
        // this way it doesn't cause the row to jump
        setTimeout(() => {
          setRowActionsOpen(currentValue => {
            if (currentValue?.id !== rowId || currentValue?.menu !== menu)
              return currentValue
            return null
          })
        }, 130)
      }
    },
    [],
  )

  const columns = useMemo<ColumnDef<T>[]>(() => {
    let cols = onRowSelectionChange
      ? [
          {
            id: 'select',
            size: 40,
            maxSize: 40,
            header: () => <Flex minWidth="24px" />,
            enableHiding: false,
            enableResizing: false,
            meta: { enableDragging: false },
          },
          ...suppliedColumns,
        ]
      : suppliedColumns

    if (getRowActions || onColumnVisibilityChange) {
      cols = [
        ...cols,
        {
          id: 'actions',
          header: ({ table: _table }) => {
            if (!onColumnVisibilityChange) return null

            // Render a placeholder (empty fragment) since the component is applied separately
            return <></>
          },
          cell: ({ row }) => {
            if (!getRowActions) return null

            const actions = getRowActions(row.original)

            return (
              <Flex gap="3" className={styles.ActionsCellContent}>
                {actions.map(action =>
                  renderRowAction(
                    row.id,
                    action,
                    onRowActionsOpenChange,
                    onClickRowAction,
                    rowActionsOpen,
                  ),
                )}
              </Flex>
            )
          },
          size: 32,
          enableHiding: false,
          enableResizing: false,
          meta: { enableDragging: false },
        },
      ]
    }

    return cols
  }, [
    onRowSelectionChange,
    suppliedColumns,
    getRowActions,
    onColumnVisibilityChange,
    onRowActionsOpenChange,
    onClickRowAction,
    rowActionsOpen,
  ])

  const [internalColumnOrder, setInternalColumnOrder] =
    useState<ColumnOrderState>(columnOrder ?? [])

  useEffect(() => {
    if (!columnOrder) return
    setInternalColumnOrder(prev =>
      prev.length === columnOrder.length &&
      prev.every((id, i) => id === columnOrder[i])
        ? prev
        : columnOrder,
    )
  }, [columnOrder])

  // Sizing mirrors the columnOrder hybrid pattern: internal state drives live
  // drag feedback, parent is only notified once sizing settles on a new value
  // while no drag is in progress (see the commit effect below) - this covers
  // both drag-end and instant changes like the header's double-click resetSize().
  const [internalColumnSizing, setInternalColumnSizing] =
    useState<ColumnSizingState>(columnSizing ?? {})
  const committedColumnSizingRef = useRef<ColumnSizingState>(columnSizing ?? {})

  useEffect(() => {
    if (!columnSizing) return
    setInternalColumnSizing(prev => {
      if (isEqual(prev, columnSizing)) return prev
      committedColumnSizingRef.current = columnSizing
      return columnSizing
    })
  }, [columnSizing])

  const normalizedRowSelectionRef = useRef<RowSelectionState>({})
  const normalizedRowSelection = useMemo(() => {
    const next = rowSelection ?? {}
    return isEqual(normalizedRowSelectionRef.current, next)
      ? normalizedRowSelectionRef.current
      : next
  }, [rowSelection])
  useEffect(() => {
    normalizedRowSelectionRef.current = normalizedRowSelection
  }, [normalizedRowSelection])

  const canSelectRow = useCallback(
    (row: Row<T>) => {
      if (row.original.isNestingGroupHeader) return false
      return (rowSelectionCondition ?? alwaysSelectable)(row)
    },
    [rowSelectionCondition],
  )

  const table = useReactTable({
    data,
    columns,
    state: {
      rowSelection: normalizedRowSelection,
      columnVisibility,
      sorting,
      columnPinning: columnPinning || {},
      columnOrder: internalColumnOrder,
      columnSizing: internalColumnSizing,
      ...(expanded !== undefined && { expanded }),
    },
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    enableRowSelection: onRowSelectionChange ? canSelectRow : false,
    // A parent is a track in its own right, not a folder - ticking it selects
    // that track only, so actions don't silently pick up its versions.
    enableSubRowSelection: false,
    onRowSelectionChange: onRowSelectionChange
      ? updater =>
          onRowSelectionChange(resolveUpdater(updater, normalizedRowSelection))
      : undefined,
    onColumnVisibilityChange: onColumnVisibilityChange
      ? updater =>
          onColumnVisibilityChange(
            resolveUpdater(updater, columnVisibility ?? {}),
          )
      : undefined,
    onSortingChange: onSortingChange
      ? updater => onSortingChange(resolveUpdater(updater, sorting ?? []))
      : undefined,
    onColumnOrderChange: updater => {
      const next = resolveUpdater(updater, internalColumnOrder)
      setInternalColumnOrder(next)
      onColumnOrderChange?.(next)
    },
    onColumnSizingChange: updater => {
      setInternalColumnSizing(prev => resolveUpdater(updater, prev))
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSubRows,
    getExpandedRowModel: getSubRows ? getExpandedRowModel() : undefined,
    // Spread conditionally: an explicit `undefined` would replace TanStack's
    // own state updater and silently break uncontrolled expansion.
    ...(onExpandedChange && {
      onExpandedChange: (updater: Updater<ExpandedState>) =>
        onExpandedChange(resolveUpdater(updater, expanded ?? {})),
    }),
    manualSorting: true,
    manualFiltering: true,
    manualPagination: true,
    // Parent-prefixed: one track can be a result and a child of several parents.
    getRowId: (row, _index, parent) =>
      parent ? `${parent.id}.${row.id}` : row.id.toString(),
    meta,
  })

  const leafColumns = useMemo(() => table.getAllLeafColumns(), [table])

  // Commit sizing to the parent whenever it settles on a new value while no
  // drag is in progress - covers both drag-end and instant resets (e.g. the
  // header's double-click resetSize()), without firing on every mousemove.
  const resizingColumn = table.getState().columnSizingInfo.isResizingColumn
  useEffect(() => {
    if (resizingColumn) return
    if (isEqual(committedColumnSizingRef.current, internalColumnSizing)) return
    committedColumnSizingRef.current = internalColumnSizing
    onColumnSizingChange?.(internalColumnSizing)
  }, [resizingColumn, internalColumnSizing, onColumnSizingChange])

  const handleColumnReorderDrop = useCallback(
    (draggedId: string, targetId: string) => {
      // Reconcile the persisted order with the live columns so newly added
      // columns (e.g. flag-gated additions) stay reorderable for users who
      // already have a saved order that predates them.
      const leafIds = table.getAllLeafColumns().map(c => c.id)
      const currentOrder = reconcileColumnOrder(internalColumnOrder, leafIds)

      const next = reorderColumns(currentOrder, draggedId, targetId)
      if (next) table.setColumnOrder(next)
    },
    [table, internalColumnOrder],
  )
  // --- Render Logic ---
  // Infinite scroll using Intersection Observer
  useEffect(() => {
    if (!hasNextPage || !onFetchNextPage || hasNextPageError) return

    const observer = new IntersectionObserver(
      entries => {
        const target = entries[0]
        // Don't trigger if fetching, loading, or no data (prevents triggering during loading states)
        if (
          target?.isIntersecting &&
          !isFetchingNextPage &&
          !loading &&
          data?.length > 0
        ) {
          onFetchNextPage()
        }
      },
      {
        root: null,
        rootMargin: '100px', // Trigger 100px before the element is visible
        threshold: 0.1,
      },
    )

    const current = sentinelRef.current
    if (current) {
      observer.observe(current)
    }

    return () => {
      if (current) {
        observer.unobserve(current)
      }
    }
  }, [
    hasNextPage,
    onFetchNextPage,
    isFetchingNextPage,
    loading,
    data?.length,
    hasNextPageError,
  ])

  // Scroll to top
  useEffect(() => {
    if (loading && !isFetchingNextPage && skeletonRef.current) {
      const tableElement = skeletonRef.current.closest('table')
      if (tableElement) {
        tableElement.scrollIntoView({ behavior: 'instant', block: 'start' })
      } else {
        // Fallback to skeleton row
        skeletonRef.current.scrollIntoView({
          behavior: 'instant',
          block: 'start',
        })
      }
    }
  }, [loading, isFetchingNextPage])

  const tableRows = table.getRowModel().rows
  const tableRowSelection = table.getState().rowSelection

  // Numbered like SelectionProvider's array - rendered rows minus section
  // labels - because Redux selection indexes into it positionally.
  const selectableIndexByRowId = useMemo(() => {
    const indexes = new Map<string, number>()
    let next = 0
    tableRows.forEach(row => {
      if (row.original.isNestingGroupHeader) return
      indexes.set(row.id, next)
      next += 1
    })
    return indexes
  }, [tableRows])

  const { selectedFullRows, selectedRowItems } = useMemo(() => {
    let selectedRowItems: Row<T>[] = []
    let selectedFullRows: Record<string, boolean> = {}

    if (!isMultiSelect) {
      selectedRowItems = tableRows.filter(r => r.getIsSelected())
      selectedFullRows = tableRowSelection || {}
    } else {
      selectedRowItems = tableRows.filter(r => {
        const index = selectableIndexByRowId.get(r.id)
        return (
          index !== undefined && selection.includes(index) && r.getCanSelect()
        )
      })
      // Row id, not track id: rowSelection is row-keyed, so keying a sub-row
      // by its track id would leave it reading back as unselected.
      selectedFullRows = selectedRowItems.reduce(
        (acc, r) => ({ ...acc, [r.id]: true }),
        {},
      )
    }

    return { selectedFullRows, selectedRowItems }
  }, [
    isMultiSelect,
    tableRows,
    tableRowSelection,
    selection,
    selectableIndexByRowId,
  ])

  // re-runs the effect when the handler is added or removed
  const hasSelectionHandler = !!onRowSelectionChange

  useEffect(() => {
    if (!selectedRowItems?.length) {
      lastRowSelectedRef.current = null
    }
  }, [selectedRowItems?.length])

  useEffect(() => {
    if (!onRowSelectionChangeRef.current) return
    if (isEqual(normalizedRowSelection, selectedFullRows)) return

    onRowSelectionChangeRef.current(selectedFullRows)
  }, [selectedFullRows, normalizedRowSelection, hasSelectionHandler])

  const boundSelectTableRowItems = useCallback(
    (_selectionId, selectionRange, selectionType) => {
      dispatch(
        selectDataTableItems(
          selectionContext.id,
          selectionRange,
          selectionType,
          location,
        ),
      )
    },
    [dispatch, location, selectionContext.id],
  )

  const handleTrackClick = useCallback(
    (
      event: Pick<MouseEvent, 'shiftKey' | 'metaKey' | 'ctrlKey'>,
      _row: Row<T>,
      flatIndex: number,
    ) => {
      const selectionId = selectionContext.id
      if (!selectionId) return
      if (!event.shiftKey && !event.metaKey && !event.ctrlKey) {
        dispatch(clearDataTableSelection(selectionId))
      }
      handleModifierKeysDataTableSelection(event, {
        selection: { [selectionId]: dataTableDataSelection },
        index: flatIndex,
        selectionId,
        selectItems: boundSelectTableRowItems,
        lastRowSelected: lastRowSelectedRef.current,
      })
      if (!event.shiftKey) {
        lastRowSelectedRef.current = flatIndex
      }
    },
    [
      dataTableDataSelection,
      selectionContext.id,
      boundSelectTableRowItems,
      dispatch,
    ],
  )

  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders()
    const colSizes: { [key: string]: number } = {}
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!
      const sanitizedId = sanitizeColumnId(header.id)
      const sanitizedColId = sanitizeColumnId(header.column.id)
      colSizes[`--header-${sanitizedId}-size`] = header.getSize()
      colSizes[`--col-${sanitizedColId}-size`] = header.column.getSize()
    }
    return colSizes
  }, [table.getState().columnSizingInfo, table.getState().columnSizing])

  return (
    <div className={styles.TableContainer}>
      {topRowContent && (
        <div
          className={styles.TopRowContent}
          data-testid="similarity-player-container"
        >
          {topRowContent}
        </div>
      )}
      <Table.Root
        variant="surface"
        size="1"
        className={cn({
          [styles.StickyTableRoot]: stickyHeader,
          [styles.TableAnimation]: !loading,
          [styles.HasTopRowContent]: !!topRowContent,
        })}
        {...{
          style: {
            ...columnSizeVars,
            position: 'relative',
            maxWidth: '100%',
          },
        }}
      >
        <SearchTable.Header
          table={table}
          stickyHeader={stickyHeader}
          onColumnVisibilityChange={onColumnVisibilityChange}
          leafColumns={leafColumns}
          onColumnReorderDrop={handleColumnReorderDrop}
          enableColumnDragging={enableColumnDragging}
        />

        <SearchTable.Body
          data={data}
          loading={loading}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          table={table}
          rowActionsOpen={rowActionsOpen}
          isPartOfMultipleSelection={isPartOfMultipleSelection}
          handleTrackClick={handleTrackClick}
          selectedRowItems={selectedRowItems}
          columns={columns}
          hasNextPageError={hasNextPageError}
          onRetryNextPage={onRetryNextPage}
          sentinelRef={sentinelRef}
          skeletonRef={skeletonRef}
          getPinningStyles={getPinningStyles}
          location={location}
          searchType={searchType}
          searchResultsSource={searchResultsSource}
          searchId={searchId}
          selectableIndexByRowId={selectableIndexByRowId}
        />
      </Table.Root>
    </div>
  )
}

export default DataTable
export type { Row } from './types'
